import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/4ebe1d4/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`Notifications are an important method of communicating with users and providing feedback. They range from granular, inline responses to user actions to system-wide messages.`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Overview</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Designing with notifications</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Notification status</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Notification types</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Considerations when designing</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Accessibility</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Related components and patterns</AnchorLink>
      <AnchorLink mdxType="AnchorLink">References</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Feedback</AnchorLink>
    </AnchorLinks>
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`It’s important to keep users informed and send notifications when there is an update or status change they should be aware of. Users should always be given appropriate and timely messages to help them understand whether they are moving towards their goal. Ensure your notifications are relevant, timely, and informative.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key principle`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Definition`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Relevant`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Notifications should be related to the user’s goals and presented in the context of what they are doing`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Timely`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Ensure users are kept up to date with prompt notifications and see critical notifications immediately`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Informative`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provide users with the context and next steps needed to understand and address the notification`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Be considerate of your users when sending a notification. Notifications that are too frequent or disruptive create negative experiences and drive users from platforms.`}</p>
    <p>{`Notifications are comprised of status and type. Their status signifies the purpose of the information being conveyed. Notification types allow you to tailor the disruptiveness of the notification to the specific situation. Notification status and type options in Carbon should be combined to create notifications that are relevant, timely, and informative for each use case.`}</p>
    <Row mdxType="Row">
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4 {...{
          "id": "notification-status"
        }}>{`Notification status`}</h4>
        <ul>
          <li parentName="ul">{`Informational`}</li>
          <li parentName="ul">{`Success`}</li>
          <li parentName="ul">{`Warning`}</li>
          <li parentName="ul">{`Error`}</li>
        </ul>
      </Column>
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4 {...{
          "id": "notification-type"
        }}>{`Notification type`}</h4>
        <ul>
          <li parentName="ul">{`Inline`}</li>
          <li parentName="ul">{`Toast`}</li>
          <li parentName="ul">{`Banner`}</li>
          <li parentName="ul">{`Notification panel`}</li>
          <li parentName="ul">{`Modal`}</li>
        </ul>
      </Column>
    </Row>
    <h2 {...{
      "id": "designing-with-notifications"
    }}>{`Designing with notifications`}</h2>
    <h3 {...{
      "id": "when-to-use"
    }}>{`When to use`}</h3>
    <p>{`Use notifications to inform users of important status changes and updates. Transparency is a core element of building user trust and is the first of Jakob Nielsen’s `}<a parentName="p" {...{
        "href": "https://www.nngroup.com/articles/ten-usability-heuristics/"
      }}>{`10 usability heuristics`}</a>{`. They should be relevant to the user and as minimally disruptive as possible. There are two major use cases for notifications: task-generated and system-generated.`}</p>
    <h4 {...{
      "id": "task-generated-notifications"
    }}>{`Task-generated notifications`}</h4>
    <p>{`Task-generated notifications are initiated in response to user action during a specific task. They give users direct, immediate feedback. They should be placed in the region of the page the user is working in and be related to the user’s action.`}</p>
    <p>{`You might send a task-generated notification when:`}</p>
    <ul>
      <li parentName="ul">{`A form is successfully submitted`}</li>
      <li parentName="ul">{`There is a problem uploading a file`}</li>
      <li parentName="ul">{`Credentials can’t be found`}</li>
    </ul>
    <h4 {...{
      "id": "system-generated-notifications"
    }}>{`System-generated notifications`}</h4>
    <p>{`These notifications are initiated by the system independent of user action. They provide updates on background system status or out-of-context events that have finished.`}</p>
    <p>{`You might send a system-generated notification when:`}</p>
    <ul>
      <li parentName="ul">{`A user loses network connection`}</li>
      <li parentName="ul">{`Planned system maintenance is coming soon`}</li>
      <li parentName="ul">{`A new report is ready`}</li>
      <li parentName="ul">{`The user’s login session is about to expire`}</li>
    </ul>
    <h3 {...{
      "id": "when-not-to-use"
    }}>{`When not to use`}</h3>
    <p>{`Only send notifications where necessary. Confine each notification to the portion of the interface and workflow it is relevant to. Being interrupted creates a frustrating and discouraging experience for users, so this should be limited as much as possible. Additionally, frequent distractions `}<a parentName="p" {...{
        "href": "https://link.springer.com/chapter/10.1007/978-1-4842-4221-6_9#Sec22"
      }}>{`lower productivity`}</a>{` and can lead to `}<a parentName="p" {...{
        "href": "https://psnet.ahrq.gov/primer/alert-fatigue"
      }}>{`alert fatigue`}</a>{`.`}</p>
    <h2 {...{
      "id": "notification-status-1"
    }}>{`Notification status`}</h2>
    <p>{`Notification status helps convey the information being communicated. These statuses correspond with a color and icon to provide a consistent, universal experience for users.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "45.80357142857143%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsSAAALEgHS3X78AAAA+ElEQVQoz62S2W6DMBBF+f9fbKVmIUpwAhjD2Ga83I5dmlaq2uYhyEfY6HDNDG6AjP+unHPlEa8pnl8jrGeQY7kHOFm7NcBzQoi5mFWm4GB4wbwKTFgqFku0WBPX0BqotMdJTWg7jdPV4DIQLj3hZmQTn1CrkHGgM16mPV4HYdzjzbSVg7tgDOYj8Jklb19Y5ISUflKef4bVdfrNy3eaR5v9l5e//5QySe4I0jvo2w5G+kNTW2F7RpLe5E2OnYJVCrrrMKsrqO9hhTTPkuG2koscRkQ/gN2A4GW+jghCYo0c3T2wvBiXBSwEokq0FlnCMvNXD595Dt8BEn3Dh5eoROsAAAAASUVORK5CYII=')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Example of notification status",
        "title": "Example of notification status",
        "src": "/static/389fe76ec86370a5cd0926eb230196fc/c801a/Status_1120.png",
        "srcSet": ["/static/389fe76ec86370a5cd0926eb230196fc/1a057/Status_1120.png 288w", "/static/389fe76ec86370a5cd0926eb230196fc/8418d/Status_1120.png 576w", "/static/389fe76ec86370a5cd0926eb230196fc/c801a/Status_1120.png 1152w", "/static/389fe76ec86370a5cd0926eb230196fc/4d515/Status_1120.png 1728w", "/static/389fe76ec86370a5cd0926eb230196fc/66720/Status_1120.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Status`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Action`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Color`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Icon`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Informational`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provide additional information to users that may not be tied to their current action or task`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Do not require immediate action and can be dismissed on a timer or persist, depending on the content`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Blue`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No icon`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Success`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Confirm a task was completed as expected`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Typically do not require further action and can be dismissed automatically or persist in a nonintrusive manner`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Green`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`checkmark filled`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Warning`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Inform users that they are taking actions that are not desirable or might have unexpected results`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Often persist until the user dismisses the notification or continues in their task`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Yellow`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`warning filled`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Error`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Inform users of an error or critical failure and optionally block the user from proceeding until the issue has been resolved`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Always persist until the user dismisses them or resolves error`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Red`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`error filled`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "notification-types"
    }}>{`Notification types`}</h2>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "45.80357142857143%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsSAAALEgHS3X78AAABfUlEQVQoz3VSYUvCUBT1b/gHsr9X9Bf60KeCvkhChjAKgiDEYk3bxlD7bDG10SSDals6p1M2dTu9+3KLJA+cPXj33nPfuXeZOI4xGAzgOA6nZVnwfR+EIAgwGnnwPA/j8RgRy6V8AuVYts1qvmDbDtegWGaxWEBRFMjyPTRNQ7VahWEYvKhUKiGbzSKX22Lcxv7BEYZDl8daTzquyzcQpRoqt3dQVA3L5RIZUo1WXQnUgAKEfr8PURRRq9UgSRJrrGI6nfJYFP3WJOAvpM90FsJnnPgzZmXCrZLwJlDD+XyOMAw5KZeYCsZxvLGYCkkg4SYkOplEjIYqyzKfp6qqOCsW0ev1VvaitKnruqjX62g0Gmg2H1BnJ939sZxs1GZbs1ebOzzOo/X49GORCZIoQdd1CILA/wh38ImLc4HfJY1TwXXbO7t77MVKOrNE0DRNFAonKJcruJT6yJ9ewXx5/l8wmRMFOu02H8O6ZVoCbb/T7aJtvOP17YPdBanGN0LpnTENW/AjAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Examples of basic notification types",
            "title": "Examples of basic notification types",
            "src": "/static/86f4bae39fe7b6bea8c1940f8b8d9d54/c801a/Notification_types_1120.png",
            "srcSet": ["/static/86f4bae39fe7b6bea8c1940f8b8d9d54/1a057/Notification_types_1120.png 288w", "/static/86f4bae39fe7b6bea8c1940f8b8d9d54/8418d/Notification_types_1120.png 576w", "/static/86f4bae39fe7b6bea8c1940f8b8d9d54/c801a/Notification_types_1120.png 1152w", "/static/86f4bae39fe7b6bea8c1940f8b8d9d54/4d515/Notification_types_1120.png 1728w", "/static/86f4bae39fe7b6bea8c1940f8b8d9d54/66720/Notification_types_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Notification type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Duration and interaction`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Inline`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provide users with nondisruptive feedback or the status of an action`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Persist until the message is resolved or dismissed by user and may include a ghost button action`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Toast`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Short, time-based messages that slide in and out of a page and provide nondisruptive information`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Disappear automatically or can be dismissed by user`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Banner`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`System or product level notifications that are not specific to a task`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Persist until dismissed by user and may include a ghost button or link`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Notification panel`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Notification center that provides users with system-generated messages`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Opened and closed by user`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Modal`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Highly disruptive notifications that provide users with critical information that needs their attention or action`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Persist and block tasks until dismissed by user. Modals allow more user action than other notifications and can include other Carbon components.`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "inline-notification"
    }}>{`Inline notification`}</h3>
    <p><a parentName="p" {...{
        "href": "https://www.carbondesignsystem.com/components/notification/usage/"
      }}>{`Inline notifications`}</a>{` are nondisruptive and confined to a specific area in the UI. Inline notifications display both task-generated and system-generated messages and persist until they are dismissed by the user or the notification is resolved. They are frequently used in conjunction with field-level messages for errors in forms or other input areas.`}</p>
    <p>{`Carbon offers low-contrast and high-contrast inline notifications. All inline notifications use a color that corresponds with their message intent and can also be accompanied by an icon to reinforce the message intent.`}</p>
    <h4 {...{
      "id": "best-practices"
    }}>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Place inline notifications near their related items.`}</li>
      <li parentName="ul">{`Inline notifications can appear at either the top or bottom of forms.`}</li>
      <li parentName="ul">{`Keep the message under two lines.`}</li>
      <li parentName="ul">{`The notification width varies based on the context and page layout.`}</li>
      <li parentName="ul">{`Be descriptive and give users clear next steps.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACJUlEQVQ4y62Ty44SQRSGeQMgcTVhARKwA+FqQoTE7o0RX0RICLIxcc8lslFhJ3E7XEzMOLow4WGGAIEAAg3dNM1FBn7rFDaDm4kLK/lTXdWnvj7nP9Um/OdhOhwOqF5eIpvNolAoIJ/Pn0TrXC73lyiuWCyiXC6fVCqVUKlUoKoqTPv9Hu/ef0D6VQbpdBqpVAqZTIaL1slk8qREIsH34vE4LBYLrFYrl9lshtvtRrfbhclIVdVW0JdLaJoGXdfvLavZbMLr9cLn88Hj8cDlciEajaLf7x+B2mKB8fgnZFmGoiiYzWZ8Xv75gCEqiUaj0eDAUCjEYQ6HA+Fw+A5IhzudDkajEYcYOoeRKI4sqtfr8Pv9iEQiHGq32xEMBo9Aagodns/n2Gw2WK/XXMbz+UxxFE8ZBgIBxGIxXjZlKUkSer3eEUieUTkGjER7ZAH5akApS4qv1WpwOp0cKggCB4qieATyhjDYdDrlGRCIRM8L5u35Hq35NWNAm83GQe5HAuwPXQiEHt952GHtbrXb/IBGoDPfaI9LW2DOPOTAapXD/H6WofMCYeECz0XWlB67NgcG1MdjKDc3kAcDyCxtZTjkUocjrGcyFhMFyph1fTLB7W6H6+tveCpKiD+TYJPewPTkIx68+IT2QD0Ct6ycLctiQ34xbc/0a7Vi7w3p2DFgq9XCVwb98f0L3l5N8fIz8PoKmC7Zr8dquPcSH/7pD94x3fLo3w06Tsr7gdPSAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Inline notification in a product",
            "title": "Inline notification in a product",
            "src": "/static/dcfc44cf82f052c9e7e63aea6d99466c/c801a/Inline_1120.png",
            "srcSet": ["/static/dcfc44cf82f052c9e7e63aea6d99466c/1a057/Inline_1120.png 288w", "/static/dcfc44cf82f052c9e7e63aea6d99466c/8418d/Inline_1120.png 576w", "/static/dcfc44cf82f052c9e7e63aea6d99466c/c801a/Inline_1120.png 1152w", "/static/dcfc44cf82f052c9e7e63aea6d99466c/4d515/Inline_1120.png 1728w", "/static/dcfc44cf82f052c9e7e63aea6d99466c/66720/Inline_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3 {...{
      "id": "toast"
    }}>{`Toast`}</h3>
    <p><a parentName="p" {...{
        "href": "https://www.carbondesignsystem.com/components/notification/usage/"
      }}>{`Toasts`}</a>{` are notifications that slide in and out, typically in the top right of the page. They are more disruptive than inline notifications and are best used with system-generated messages that do not correspond to a specific section of the UI. Toasts disappear automatically after a set amount of time but can also be dismissed by the user.`}</p>
    <p>{`Carbon toasts can be either low-contrast or high-contrast. Their color should correspond with the message intent and they can also use an icon to convey the message intent.`}</p>
    <h4 {...{
      "id": "best-practices-1"
    }}>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Multiple toasts stack horizontally, with the newest appearing at the top of the list.`}</li>
      <li parentName="ul">{`Keep messages clear and concise and only show two lines of content.`}</li>
      <li parentName="ul">{`Toasts dismiss after 5 seconds.`}</li>
      <li parentName="ul">{`Limit toasts to two lines of content.`}</li>
      <li parentName="ul">{`When necessary, give toasts an “expand” or “show more” option to accommodate longer content.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACT0lEQVQ4y62TS08TURSAJwQoUGmpQistFGvUFPwBJi4pJmBSw0Jd8gv8DZogmPiYtlDER+IKg26ICW6MiQbjxsS4MSQkRIyY2mLTx3T6mL4/Z24LEZXowpt8c+49557HPXOvxH8eUr1eZ2VlhUAgQCgUEjIYDO4hy/Lv3JG5MTPLzPR1wfS1qwRu30RR0ki1Wg2fz0dnZydWqxWLxUJPT4/AZrNhNpv30dXVhcVq4czoWUbPjzHmn8B3cQr/1BW2I3GkarXK5OQkXq+X4eFhnE6noL+/X7C7FricOOwOTnhP8vrrOzbUz2xqEWIofKpk+JYoNQL6/RfweDyMjIzgdruFs91ux+Fw7Oeog77eXoaOH+Ppm2e8Wn/Ly49rrG2t82Jjk0g8bxy5zsT4ON2HzLiale2r6g+4XC4G3YO4hzx4Bmz0nb7EwOUPfNnRjAor+M5NYDniwnq4j44OEyaTSZcdoq/t7e1CGrpdfVtbG62tOi0SLd2nkLyPaPG9ZytabBx5dfU5S4+Xuf/gIeFweO9Ph8Pz+lzm3uIi83NzgoWFsNAZ9pB8C/nuE2aXIgSXN0hny0gH3adUWiGeSFH9p9tXaVCvNQKm02mi0ajAmGcyCluxAtGUxo6S18kRy2SJqyqxpMp2QiOuFIjp+p2MblMLJNQctWozYDabJZlMkkqlUHWnXC5HPJkV8teh5vJ8TyQpl4pNTV18y+UyRvvESymVSnsUi0WBphVEIk3T0MRaE07GHiNRvlBo7tMo6fpKpYLxSKS/dcdI+DMH2XbHD8NC/Rkn+fP7AAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Toast notification in a product",
            "title": "Toast notification in a product",
            "src": "/static/69e840a9208ba3ee7336e5451cd22601/c801a/Toast_1120.png",
            "srcSet": ["/static/69e840a9208ba3ee7336e5451cd22601/1a057/Toast_1120.png 288w", "/static/69e840a9208ba3ee7336e5451cd22601/8418d/Toast_1120.png 576w", "/static/69e840a9208ba3ee7336e5451cd22601/c801a/Toast_1120.png 1152w", "/static/69e840a9208ba3ee7336e5451cd22601/4d515/Toast_1120.png 1728w", "/static/69e840a9208ba3ee7336e5451cd22601/66720/Toast_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3 {...{
      "id": "banner"
    }}>{`Banner`}</h3>
    <p>{`Banners take over the top of an interface to show notifications general to the product or system, not a specific task. They persist until they are dismissed by the user. Banners can include a ghost button or link. Depending on the message, the user resolving a product or system issue may also dismiss the banner (e.g. updating necessary account information). Banners may also persist across multiple sessions.`}</p>
    <h4 {...{
      "id": "best-practices-2"
    }}>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Banners should be placed at the top of the content area they relate to.`}</li>
      <li parentName="ul">{`Place system-wide messages directly below the main header or navigation bar.`}</li>
      <li parentName="ul">{`Only show one banner at a time.`}</li>
    </ul>
    <p>{`Carbon currently does not have a banner component, but if your team has developed one it can be contributed and made available to others.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAAByklEQVQ4y61TPW/bMBDVr7XcyM5QeLVsb0YB/4JOzj/o0Kl75wJdWjSZEkuWREvWh/VJ8vWOshLDCdKlBB7ujrz3+EhRFv7zsLTW2G63cN05VqsVFosFFsulwZLguu674P75fI71eo0kSWAppbDZbOA4E0ycGzg3HzCdTjCdOLilOB7bGNs2bHsEe3SBcz0617PZDEKI3mFeASLXOKQV5RJVB1Do4xlUQhPUBa4HaxlBEWfwggNCihFBJDkiwhCjOEecljjmtUGcVUiLCkFS49vPBl9/tPj+u4ZWEhYrR2GAh/s/JobBHnvfP+cBfM9DsN8jpNrzdtg9PSKgHhF4+PXg4+NdDPtzjE9fjsa3EeTLDMMIeU5Okhg7/xFpmhr4JB7TepaR00iQWEA5nUIcaCMfsj5CNym6KoWk72GO3DQNqqqCUtrc1KnOnu+l67o3n8fAk8RhVicVpJS9IIvxrm3bolMt3dOBFhX4BZxOJ0PkPhZnEufcWxSFmevr7kWQCWVZmkWlJYoyM26HNY4M3mDImdyfSj2f5JXDuq7Jeoski6BkT2aH7GYQGURZgB3y3CtBTgYw4ZI4zA8OL3Hdx7n13n/Jjf/Kr+u/fTS+R2avRWUAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Banner notification in a product",
            "title": "Banner notification in a product",
            "src": "/static/eeeeefe946efb70cab7b2e756dec5499/c801a/Banner_1120.png",
            "srcSet": ["/static/eeeeefe946efb70cab7b2e756dec5499/1a057/Banner_1120.png 288w", "/static/eeeeefe946efb70cab7b2e756dec5499/8418d/Banner_1120.png 576w", "/static/eeeeefe946efb70cab7b2e756dec5499/c801a/Banner_1120.png 1152w", "/static/eeeeefe946efb70cab7b2e756dec5499/4d515/Banner_1120.png 1728w", "/static/eeeeefe946efb70cab7b2e756dec5499/66720/Banner_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3 {...{
      "id": "notification-panel"
    }}>{`Notification panel`}</h3>
    <p>{`Notification panels are helpful for users who receive a large number of system-generated notifications or may need to reference their notifications later. Notification panels are used in conjunction with toasts to alert users of notifications as they come in.`}</p>
    <p>{`Notification panels are a great solution to ensure that users can access and read all notifications without cluttering the screen with many persistent notifications. In addition to being useful, notification centers address some accessibility considerations with screen readers.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://www.carbondesignsystem.com/components/UI-shell-right-panel/usage"
      }}>{`right panel`}</a>{` in the Carbon UI shell supports a notification center. Carbon does not currently have a notification panel component, but if your team has developed one it can be contributed and made available to others.`}</p>
    <h4 {...{
      "id": "best-practices-3"
    }}>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Give users the ability to manage notification preferences.`}</li>
      <li parentName="ul">{`Don’t send the same notification multiple times if users don’t interact with it.`}</li>
      <li parentName="ul">{`List notifications in chronological order.`}</li>
      <li parentName="ul">{`Notifications can be grouped by source or urgency.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.28571428571428%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACPUlEQVQ4y62Tz2saQRTHPQqJEAj+ColCjIoXL/kz/D8UEu3Bi+TgDzyba+7eSqmF2qTN/5E0UAL+QBNTu0Z3153d2Z3db2fGmKSU9lD64PFmZx6fee/7Zn34z+bzPA/tdhvVahWNRgP1el3Gtddqtd9c5DabTZyenqLVasl4dnYGVVXhc11XbpRKJRSLRRwdHcl4fHzM996gUCggz73w5Pl8Xubmcjlsbm5ia2sLGxsbiMViGAwG8P1ra5eXl4jH40in0wiHw0ilUiugaJkQAnWhQl8SWBaF4zgwTQt3CsH9jII6Lke4YIxB0zQJ7Ha7iEQi2N3dxfb29gtQHC51DQ+TByiaA41DPc8FtR3cTmzcjCkG301MVQbb8UAplcCLi8/IZDI4PDzE3t4ekskk+v3+CjibKRgOR9AIkxUKs3mVt/cmbkYmvt1Z8rJVN4Y8Pz//hGg0KmHBYFC2/gz8MZ2i3xvCsJiszHU9Cfw6orLCBfFWwnHgQicgDvCu08XBwQGy2azUMJFIvAB1XcdoPMHCYFAWJkxqSx2vhgTjqQ7DWD6NwsOjyvW2gLfvu9jZiWJ/f19qKeCvNNShKI/QTQaDUDAOs3mlisYlsPlAuKaias/lQzEoOA+dj1+eNRTVifWvU+aP0jSJnKRt2zKCA1we2Sufz+f8nOJDp4NAIIBQKAS/3y/17PV6K6BIFC1SDjItS7p48Iy5MgoXeevLhV1dX6NSqeDk5ATlcln+ObPZ7M8Pew14vV5//81+AtWhXhZhvVyVAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Notification panel in a product",
            "title": "Notification panel in a product",
            "src": "/static/3e2d32e9d8ad3c77c98d65c24c7bf31c/c801a/Panel_1120.png",
            "srcSet": ["/static/3e2d32e9d8ad3c77c98d65c24c7bf31c/1a057/Panel_1120.png 288w", "/static/3e2d32e9d8ad3c77c98d65c24c7bf31c/8418d/Panel_1120.png 576w", "/static/3e2d32e9d8ad3c77c98d65c24c7bf31c/c801a/Panel_1120.png 1152w", "/static/3e2d32e9d8ad3c77c98d65c24c7bf31c/4d515/Panel_1120.png 1728w", "/static/3e2d32e9d8ad3c77c98d65c24c7bf31c/66720/Panel_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3 {...{
      "id": "modal"
    }}>{`Modal`}</h3>
    <p>{`Modals interrupt the user and pause their current task. They are highly disruptive to users and should be used sparingly. Only use a modal when the message is critical and needs the user’s immediate attention or action. Modals persist until users engage with them and only disappear when dismissed by the user.`}</p>
    <h4 {...{
      "id": "best-practices-4"
    }}>{`Best practices:`}</h4>
    <ul>
      <li parentName="ul">{`Only display one modal notification at a time.`}</li>
      <li parentName="ul">{`Use modals when you need to immediately interrupt a user’s task.`}</li>
      <li parentName="ul">{`Give users clear steps to resolve and dismiss the notification.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "64.375%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACPklEQVQ4y62SS2saURiGs28LlnjDeKmtbYRC60Z0oo6Ozs1YjJFESLwVGkIN/g+DLl3EWxcus8p/6E/oogG7l5BAaNWaQNW33xwTE2i7KGTg4TtnZnjO+S5LeOBnaTab4fj4GJVKBbVaDdVq9T+poXJ4iHq9jsFggKXpdIpkMgm9Xg+73Q6r1cqw2Wxsr8W/Mf9nBc8cNqxYzPC8fYN+vz+/YblcRiAQgCRJiMVijHA4DEEQ/omiyOAFFd5QHB6/hHcb27i4OJ/fsFQqwefzMQnP84hEIshkMtjZ2cHu7i4KhQIK+fyCbDYLWRLh8fJ45RHgfB1CVNm4Ex4clOD3c3RylKQ84vE4Tk9PcXl5yRiPxxhdXWFIcUKF/3l9jQ97ewgGOMhiFEKER3ozhfNzEmqdKRaLMJnNeP7CxerjdrvR6/UwmUwwGo0Yk+EI08GQbGP8+v4DH/f3WTaKorCLbKbTd8JcLodHj5/AaDJjeXkZRqMRiUSCTt1EMpVChvgsruNbQMTXtSi+EO9jIsKUtiLJrKZ/CJ/qdNS5FVgsFobBYMBLlwuhEE+pBZEmtjliLYgMrVVqnEhNlAlNmL4vzFOhdUxoXQi1MVpdXUV8PQFZUSFSXWM3iOtxKKoKldJVtb0oYmtray7UxkbrptFogtPppLlywEHc1pLjOGqYH5wGTQJ3s/bfQt+9Xi9kWcbZ2dlceHJygqOjBjqdT0QH7XbnJrbRbDbRarVYbDQai/V9tPfdbhfD4XCe8kM+vwFvK4voOTdpjQAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Modal notification in a product",
            "title": "Modal notification in a product",
            "src": "/static/e483dfe4ed83c2025927215be86a1509/c801a/Modal_1120.png",
            "srcSet": ["/static/e483dfe4ed83c2025927215be86a1509/1a057/Modal_1120.png 288w", "/static/e483dfe4ed83c2025927215be86a1509/8418d/Modal_1120.png 576w", "/static/e483dfe4ed83c2025927215be86a1509/c801a/Modal_1120.png 1152w", "/static/e483dfe4ed83c2025927215be86a1509/4d515/Modal_1120.png 1728w", "/static/e483dfe4ed83c2025927215be86a1509/66720/Modal_1120.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2 {...{
      "id": "considerations-when-designing"
    }}>{`Considerations when designing`}</h2>
    <h3 {...{
      "id": "notification-priority"
    }}>{`Notification priority`}</h3>
    <p>{`Notifications range in their priority and therefore should vary in their disruptiveness to users. It is important to match the urgency and priority of the information being communicated to the visual style and behavior of the notification.`}</p>
    <p>{`Carbon offers two visual styles for inline and toast notifications. The high-contrast style is more visually disruptive and should be used for notifications that are urgent or critical. The low-contrast style is better for supplemental messaging or other low priority use cases. Toast and inline notifications can use different styles, but you should never mix styles within the variations.`}</p>
    <h3 {...{
      "id": "user-action"
    }}>{`User action`}</h3>
    <h4 {...{
      "id": "optional-action"
    }}>{`Optional action`}</h4>
    <p>{`Notifications can include a ghost button that lets users interact with them. Taking action on these notifications should be optional and should not block the user from continuing with their current task. This action frequently takes users to a flow or page related to the message, where they can resolve the notification. All notifications should be dismissed when users interact with them.`}</p>
    <h4 {...{
      "id": "required-action"
    }}>{`Required action`}</h4>
    <p>{`Some notifications must block users from dismissing the notification or continuing with their task until the message has been addressed. This is common in forms when there are errors or empty input fields. Notifications that prevent users from continuing should be relevant to the current task and provide the user with the steps needed to resolve them or continue with their current task.`}</p>
    <p>{`Blocking users from continuing with a task is disruptive and hinders the overall experience, so these notifications should only be used when it is critical the user sees the notification or the user needs to take action right away.`}</p>
    <h3 {...{
      "id": "notification-message"
    }}>{`Notification message`}</h3>
    <p>{`It’s important to consider the user’s context when writing notification messages. Because task-generated notifications are sent in response to user action, it isn’t necessary to give users extensive background information. Conversely, system-generated notifications are generally not relevant to the user’s current task so you should ensure the user has sufficient context to understand the notification.`}</p>
    <p>{`Message content should be clear and concise. If a user is expected to take an action, ensure they know how to take that action.`}</p>
    <p>{`Error messages should be diagnosable and provide steps to resolve the issue. Avoid using technical language that is unfamiliar to the user.`}</p>
    <h2 {...{
      "id": "accessibility"
    }}>{`Accessibility`}</h2>
    <p><strong parentName="p">{`Only move a screen reader’s focus for the user when a notification is critical.`}</strong>{` Use `}<inlineCode parentName="p">{`aria-live`}</inlineCode>{` regions to update the user as notifications come in while also being conscious of the user’s needs. Using `}<inlineCode parentName="p">{`aria-live="assertive"`}</inlineCode>{` informs screen reader users that a notification is present without moving focus to the notification and is recommended for most high priority notifications. If a notification is not a priority, use `}<inlineCode parentName="p">{`aria-live="polite"`}</inlineCode>{` instead. This delays the announcement of the notification until the user finishes their current task so they are not interrupted.`}</p>
    <p><strong parentName="p">{`It should be easy to navigate to the notification using only a keyboard.`}</strong>{` Using `}<inlineCode parentName="p">{`TAB`}</inlineCode>{` moves the screen focus to the notification and pressing `}<inlineCode parentName="p">{`SPACE`}</inlineCode>{` or `}<inlineCode parentName="p">{`ENTER`}</inlineCode>{` dismiss the notification.`}</p>
    <p><strong parentName="p">{`Don’t use notifications that dismiss on a timer for critical or emergency messages.`}</strong>{` Some users with disabilities need more time to read or interact with messages and timed toasts may not provide sufficient time.
`}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/WCAG21/Understanding/no-timing.html"
      }}>{`WCAG 2.1 success criterion 2.2.4 (AAA)`}</a></p>
    <p><strong parentName="p">{`Users should be able to manage or limit noncritical notifications.`}</strong>{` This gives users the control to reduce the number of distractions or disruptions they receive, which is particularly helpful for users with cognitive limitations.
`}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/WCAG21/Understanding/interruptions.html"
      }}>{`WCAG 2.1 success criterion 2.2.3 (AAA)`}</a></p>
    <h2 {...{
      "id": "related-components-and-patterns"
    }}>{`Related components and patterns`}</h2>
    <Row mdxType="Row">
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4 {...{
          "id": "components"
        }}>{`Components`}</h4>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/modal/usage"
            }}>{`Modal`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/notification/usage"
            }}>{`Notification`}</a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "/components/UI-shell-right-panel/usage"
            }}>{`UI shell`}</a></li>
        </ul>
      </Column>
      <Column colSm={2} colMd={2} colLg={4} mdxType="Column">
        <h4 {...{
          "id": "patterns"
        }}>{`Patterns`}</h4>
        <ul>
          <li parentName="ul">{`Dialogs`}</li>
          <li parentName="ul">{`Error states (future)`}</li>
          <li parentName="ul">{`Forms`}</li>
        </ul>
      </Column>
    </Row>
    <p>{`The Error state pattern is currently being planned. If you would like to contribute, please see our `}<a parentName="p" {...{
        "href": "https://www.carbondesignsystem.com/how-to-contribute/pattern"
      }}>{`guidelines for contributions`}</a>{`.`}</p>
    <h2 {...{
      "id": "references"
    }}>{`References`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://psnet.ahrq.gov/primer/alert-fatigue"
        }}>{`Alert Fatigue`}</a>{` (Patient Safety Network, 2019)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-1.1/#aria-live"
        }}>{`Aria Live Regions`}</a>{` (W3C, 2017)`}</li>
      <li parentName="ul">{`Duncan P. Brumby, Christian P. Janssen, and Gloria Mark, `}<a parentName="li" {...{
          "href": "https://link.springer.com/chapter/10.1007/978-1-4842-4221-6_9"
        }}>{`How Do Interruptions Affect Productivity?`}</a>{` (Rethinking Productivity in Software Engineering, 2019)`}</li>
      <li parentName="ul">{`Kim Flaherty, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/indicators-validations-notifications/"
        }}>{`Indicators, Validations, and Notifications`}</a>{` (Nielsen Norman Group, 2015)`}</li>
      <li parentName="ul">{`Aurora Harley, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/visibility-system-status/"
        }}>{`Visibility of System Status`}</a>{` (Nielsen Norman Group, 2018)`}</li>
      <li parentName="ul">{`Jakob Nielsen, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/ten-usability-heuristics/"
        }}>{`10 Usability Heuristics for User Interface Design`}</a>{` (Nielsen Norman Group, 1994)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/standards-guidelines/wcag/"
        }}>{`Web Content Accessibility Guidelines`}</a>{` (W3C, 2018)`}</li>
    </ul>
    <h2 {...{
      "id": "feedback"
    }}>{`Feedback`}</h2>
    <p>{`Help us improve this pattern by providing feedback, asking questions, and leaving any other comments on `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-website/issues/new?assignees=&labels=feedback&template=feedback.md"
      }}>{`GitHub`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      